import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Badge, FormControl } from "react-bootstrap";
import { GripVertical } from "react-bootstrap-icons";
import { APP_URL } from "../helpers/constants";
import { groupEntityTableValues } from "../helpers/global";
import AppTable from "./app-table/AppTable";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  background: isDragging ? "#f7ebd6" : "white",
  ...draggableStyle,
});

const getValue = (admin, header) => {
  let { valueModifier } = header;
  if (valueModifier) {
    return valueModifier(admin) || "-";
  }
  let value =
    typeof admin[header.key] !== "object"
      ? admin[header.key]
      : admin[header.key]?.value;

  if (header.type !== "boolean" && !value) return "-";
  if (Array.isArray(value) && !value.length) return "-";

  if (Array.isArray(value) && header.key !== "videoUrls") {
    value = value.join(", ");
  }

  let returnElement =
    typeof value === "object" ? value[header.objectKey] : value;

  if (header.key === "videoUrls") {
    value = value.map((e, i) => {
      return (
        <a href={e} target="_blank">
          {i !== 0 && ","}Link {i + 1}{" "}
        </a>
      );
    });

    return value;
  }

  switch (header.type) {
    case "email":
      returnElement = (
        <a onClick={(e) => e.stopPropagation()} href={`mailto:${value}`}>
          {value}
        </a>
      );

      break;
    case "phone":
      returnElement = (
        <a onClick={(e) => e.stopPropagation()} href={`tel:${value}`}>
          {value}
        </a>
      );
      break;
    case "date":
      returnElement = moment(value).format("MMM DD YYYY");
      break;
    case "boolean":
      returnElement = returnElement ? "Yes" : "No";
      break;
    case "number":
      returnElement = returnElement.toFixed(header.fixedDigits || 2);
      break;
    case "appLink":
      returnElement = (
        <a target="_blank" href={`${APP_URL}${value}`}>
          {value?.toString()?.replace("[a-f\\d]{24}", "<id>")}
        </a>
      );
      break;
  }

  if (header.showAsBadge) {
    returnElement = (
      <Badge
        className="p-1 px-2"
        style={{ fontSize: 12 }}
        variant={header.badgeVariant || "primary"}
      >
        {returnElement}
      </Badge>
    );
  }

  if (header.linkToKey) {
    let url = admin[header.linkToKey] || "";

    if (header.key !== "videoUrls") {
      if (!url?.startsWith("https://") && !url.startsWith("http://")) {
        url = "https://" + url;
      }
    }

    returnElement = (
      <a target="_blank" href={url}>
        {returnElement}
      </a>
    );
  }

  return returnElement;
};

const EntityTable = ({
  headers,
  data = [],
  rowKeyField,
  searchQueryKeys = [],
  actionCell,
  additionalComponents,
  additionalHeaderComponentForGroupValues,
  toggleShowMoreSection = null,
  groupBy,
  groupValues,
  draggable,
  onListSort,
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [query, setQuery] = useState("");

  const allowDragging = useMemo(() => draggable && !groupBy && !query.trim(), [
    draggable,
    query,
  ]);

  useEffect(() => {
    const q = query.trim().toLowerCase();

    if (q.length === 0) {
      if (groupBy && groupValues) {
        setFilteredData(groupEntityTableValues(data, groupBy, groupValues));
      } else {
        setFilteredData([...data]);
      }
      return;
    }

    const filteredDatabyQuery =
      data &&
      data.filter((d) =>
        searchQueryKeys.some((key) => {
          if (typeof key === "string") {
            return (
              d[key] &&
              d[key]
                .trim()
                .toLowerCase()
                .includes(q)
            );
          } else if (Array.isArray(key) && key.length === 2) {
            const [parentKey, childKey] = key;
            const nestedValue = d[parentKey] && d[parentKey][childKey];
            if (Array.isArray(nestedValue)) {
              return nestedValue.some((val) =>
                val
                  .trim()
                  .toLowerCase()
                  .includes(q)
              );
            } else if (typeof nestedValue === "string") {
              return nestedValue
                .trim()
                .toLowerCase()
                .includes(q);
            }
          }
          return false;
        })
      );

    if (groupBy && groupValues) {
      setFilteredData(
        groupEntityTableValues(filteredDatabyQuery, groupBy, groupValues)
      );
    } else {
      setFilteredData([...filteredDatabyQuery]);
    }
  }, [query, data]);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    let movedItems = reorder(
      data,
      result.source.index,
      result.destination.index
    );
    onListSort && onListSort(movedItems);
  };

  return (
    <>
      <div className="d-flex mb-1">
        <div className="flex-grow-1">
          <FormControl
            size={"sm"}
            placeholder="Quick search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {additionalComponents && (
          <div className="ml-2">{additionalComponents()}</div>
        )}
      </div>
      <AppTable>
        <thead>
          <tr className="text-white bg-dark">
            {allowDragging && <th className="bg-primary tiny-text"> </th>}
            {headers.map(({ name }) => (
              <th
                style={{ minWidth: 100 }}
                key={name}
                className="bg-primary tiny-text"
              >
                {name}
              </th>
            ))}
            {actionCell && (
              <th style={{ minWidth: 70 }} className="bg-primary tiny-text">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable isDropDisabled={!allowDragging} droppableId="droppable">
            {(provided) => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {filteredData?.length > 0 ? (
                  <>
                    {filteredData.map((dataObj, index) => {
                      return (
                        <>
                          {/* Group Header */}
                          {groupBy && dataObj?.newGroupStarts && (
                            <tr className="bg-primary-light text-dark">
                              <td colSpan={headers.length + 1}>
                                {additionalHeaderComponentForGroupValues &&
                                  additionalHeaderComponentForGroupValues(
                                    dataObj?.[groupBy]
                                  )}
                              </td>
                            </tr>
                          )}

                          {(!groupBy ||
                            (toggleShowMoreSection &&
                              toggleShowMoreSection[dataObj[groupBy]])) && (
                            <Draggable
                              isDragDisabled={!allowDragging}
                              key={
                                typeof dataObj[rowKeyField] !== "object"
                                  ? dataObj[rowKeyField]
                                  : dataObj[rowKeyField]?.value
                              }
                              draggableId={
                                "q-" +
                                (typeof dataObj[rowKeyField] !== "object"
                                  ? dataObj[rowKeyField]
                                  : dataObj[rowKeyField]?.value)
                              }
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  )}
                                >
                                  {allowDragging && (
                                    <td style={{ width: "5%" }}>
                                      <GripVertical />
                                    </td>
                                  )}

                                  {headers.map((header) => (
                                    <td key={header.name} className="tiny-text">
                                      {getValue(dataObj, header)}
                                    </td>
                                  ))}
                                  {actionCell && (
                                    <td>
                                      {actionCell(
                                        typeof dataObj["option"] !== "object"
                                          ? dataObj
                                          : dataObj["option"]
                                      )}
                                    </td>
                                  )}
                                </tr>
                              )}
                            </Draggable>
                          )}
                        </>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={actionCell ? headers.length + 2 : headers.length}
                    >
                      <h6 className="text-muted text-center mb-0 tiny-text">
                        Nothing to show
                      </h6>
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </AppTable>
    </>
  );
};

export default EntityTable;
